import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Spinner,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from 'reactstrap';
import { useParams } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import classnames from 'classnames';
import { ContractTemplate } from './ContractTemplate';
import ContractOverview from './ContractOverview';
import ChatMessage, { ChatMessageProps } from '../DashboardProject/ChatMessage';
import SignaturePad from './SignaturePad';
import { ContractTemplateProps, Milestone } from './Contract.interface';
import { ensureAddressObject } from '../../models/Address.interface';
import '../DashboardProject/styles.css';
import { useUser } from '../../hooks';

const ContractDetail = () => {
    const { contractId } = useParams<{ contractId: string }>();
    const [contractData, setContractData] = useState<ContractTemplateProps | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(1);
    const [userName, setUserName] = useState("Default");
    const [newMessage, setNewMessage] = useState('');
    const [chatMessages, setChatMessages] = useState<ChatMessageProps[]>([]);
    const [currentUser, setCurrentUser] = useState<string | null>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const { currentUserId, isContractor } = useUser();


    const handleSignatureSave = async (url: string) => {
        if (!contractData || !currentUserId) {
            console.error('Missing required data:', { contractData: !!contractData, currentUserId: !!currentUserId });
            return;
        }
    
        if (!contractId) {
            console.error('Missing contractId');
            return;
        }
    
        const firestore = firebase.firestore();
        const contractRef = firestore.collection('contracts').doc(contractId);
        const now = new Date().toISOString();
    
        try {
            // Validate user permissions
            if (currentUserId !== contractData.ownerId && currentUserId !== contractData.contractorId) {
                console.error('User not authorized to sign contract');
                return;
            }
    
            const updates: Record<string, any> = {};
            const isOwner = currentUserId === contractData.ownerId;
            const milestoneId = isOwner ? 'signing-homeowner' : 'signing-contractor';
    
            // Set signature and date
            updates[isOwner ? 'ownerSignature' : 'contractorSignature'] = url;
            updates[isOwner ? 'ownerSignatureDate' : 'contractorSignatureDate'] = now;
    
            // Validate and update milestones
            if (!Array.isArray(contractData.milestones)) {
                console.error('Invalid milestones data:', contractData.milestones);
                throw new Error('Invalid milestones data structure');
            }
    
            const updatedMilestones = contractData.milestones.map(milestone => {
                if (milestone.id === milestoneId) {
                    const updatedMilestone = {
                        ...milestone,
                        status: 'completed' as const,
                        date: now,
                        id: milestone.id,
                        sequence: milestone.sequence,
                        title: milestone.title,
                        type: milestone.type,
                        assigneeId: milestone.assigneeId,
                        assigneeType: milestone.assigneeType
                    };
    
                    if (milestone.description) {
                        updatedMilestone.description = milestone.description;
                    }
    
                    if (milestone.action) {
                        updatedMilestone.action = {
                            ...milestone.action,
                            disabled: true
                        };
                    }
    
                    return updatedMilestone;
                }
                return milestone;
            });
    
            // Validate the updated milestones
            if (!updatedMilestones.every(m => m.id && m.sequence && m.title && m.type)) {
                throw new Error('Invalid milestone data after update');
            }
    
            updates.milestones = updatedMilestones;
    
            // Perform the update
            await contractRef.update(updates);
    
            // Update local state
            setContractData(prevData => {
                if (!prevData) return null;
                
                return {
                    ...prevData,
                    ...(isOwner ? {
                        ownerSignature: url,
                        ownerSignatureDate: now
                    } : {
                        contractorSignature: url,
                        contractorSignatureDate: now
                    }),
                    milestones: updatedMilestones
                };
            });
    
        } catch (error: unknown) {
            console.error('Error details:', error);
            
            if (error && typeof error === 'object' && 'code' in error) {
                const firebaseError = error as { code: string; message?: string };
                switch (firebaseError.code) {
                    case 'permission-denied':
                        throw new Error('You do not have permission to update this contract');
                    case 'not-found':
                        throw new Error('Contract not found');
                    default:
                        throw new Error(`Firebase error: ${firebaseError.message || 'Unknown error'}`);
                }
            }
            
            throw new Error(error instanceof Error ? error.message : 'Unknown error occurred while saving signature');
        }
    };

    const handleMessageSubmit = async () => {
        if (newMessage.trim() === '' || !contractData) return;

        const firestore = firebase.firestore();
        const contractRef = firestore.collection('contracts').doc(contractId);

        const newChatMessage: ChatMessageProps = {
            message: newMessage,
            role: currentUser === contractData.contractorName ? 'Contractor' : 'User',
            timestamp: new Date().toISOString(),
            displayName: userName
        };

        try {
            await contractRef.update({
                chatMessages: firebase.firestore.FieldValue.arrayUnion(newChatMessage)
            });

            setChatMessages(prevMessages => [...prevMessages, newChatMessage]);
            setNewMessage('');

            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const fetchContractData = useCallback(async () => {
        if (!contractId) return;

        setLoading(true);
        const firestore = firebase.firestore();
        const contractDoc = await firestore.collection('contracts').doc(contractId).get();

        if (contractDoc.exists) {
            let data = contractDoc.data() as ContractTemplateProps;

            data.id = contractId;
            // Convert string addresses to Address objects
            data.ownerAddress = ensureAddressObject(data.ownerAddress);
            data.contractorAddress = ensureAddressObject(data.contractorAddress);
            data.propertyAddress = ensureAddressObject(data.propertyAddress);

            // Set default values
            data.terminationNoticeDays = data.terminationNoticeDays || 14;
            data.delayDays = data.delayDays || 30;
            data.inspectionDays = data.inspectionDays || 7;
            data.removalPeriod = data.removalPeriod || 14;



            // Fetch associated job
            if (data.jobId) {
                console.group('Contract/Job Data');
                console.log("Contract Data:", data);
                const jobDoc = await firestore.collection('jobs').doc(data.jobId).get();
                if (jobDoc.exists) {
                    console.log("Associated Job:", jobDoc.data());
                } else {
                    console.log("Associated Job not found");
                }
            }

            // Fetch associated bid
            if (data.bidId) {
                const bidDoc = await firestore.collection('jobs').doc(data.jobId).collection('bids').doc(data.bidId).get();
                if (bidDoc.exists) {
                    console.log("Associated Bid:", bidDoc.data());
                } else {
                    console.log("Associated Bid not found");
                }
            }
            console.groupEnd();

            setContractData(data);
            setChatMessages(data.chatMessages || []);
        }
        setLoading(false);
    }, [contractId]);

    useEffect(() => {
        fetchContractData();
    }, [fetchContractData]);

    const toggleTab = (tab: number) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    if (loading) {
        return (
            <div className="page-content d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (!contractData) {
        return (
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Contract Detail" pageTitle="Dashboard" />
                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <h4>Contract not found</h4>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Contract Detail" pageTitle="Dashboard" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h4 className="card-title mb-0">Contract Details</h4>
                                </CardHeader>
                                <CardBody className="form-steps">
                                    <form className="horizontal-navs-step">
                                        <div className="step-arrow-nav mb-4">
                                            <Nav
                                                className="nav-pills custom-nav nav-justified"
                                                role="tablist"
                                            >
                                                <NavItem>
                                                    <NavLink
                                                        href="#"
                                                        id="steparrow-contract-overview-tab"
                                                        className={classnames({ active: activeTab === 1 })}
                                                        onClick={() => toggleTab(1)}
                                                    >
                                                        Overview
                                                    </NavLink>
                                                </NavItem>
                                                <NavItem>
                                                    <NavLink
                                                        href="#"
                                                        id="steparrow-contract-messages-tab"
                                                        className={classnames({ active: activeTab === 2 })}
                                                        onClick={() => toggleTab(2)}
                                                    >
                                                        Messages
                                                    </NavLink>
                                                </NavItem>
                                                <NavItem>
                                                    <NavLink
                                                        href="#"
                                                        id="steparrow-contract-detail-tab"
                                                        className={classnames({ active: activeTab === 3 })}
                                                        onClick={() => toggleTab(3)}
                                                    >
                                                        Contract Detail
                                                    </NavLink>
                                                </NavItem>
                                            </Nav>
                                        </div>

                                        <TabContent activeTab={activeTab}>
                                            <TabPane tabId={1}>
                                                <ContractOverview
                                                    contract={contractData}
                                                    onNavigateToTab={(tab) => setActiveTab(tab)} />
                                            </TabPane>

                                            <TabPane tabId={2}>
                                                <Row>
                                                    <Col lg={12} className="d-flex flex-column" style={{ height: 'calc(100vh - 300px)' }}>
                                                        <div className="chat-container flex-grow-1 overflow-auto mb-3" ref={chatContainerRef}>
                                                            {chatMessages.map((message, index) => (
                                                                <div key={index} className={`message-wrapper ${message.displayName === userName ? 'right' : 'left'}`}>
                                                                    <ChatMessage
                                                                        message={message.message}
                                                                        role={message.role}
                                                                        timestamp={message.timestamp}
                                                                        displayName={message.displayName}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <div className="message-input mt-auto">
                                                            <textarea
                                                                value={newMessage}
                                                                onChange={(e) => setNewMessage(e.target.value)}
                                                                placeholder="Enter your message"
                                                                className="textarea mb-2"
                                                                rows={3}
                                                            />
                                                            <div className="d-flex justify-content-end">
                                                                <button
                                                                    type="button"
                                                                    onClick={handleMessageSubmit}
                                                                    className="submit-button"
                                                                >
                                                                    Submit
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </TabPane>

                                            <TabPane tabId={3}>
                                                <Row className="justify-content-center">
                                                    <Col lg={12}>
                                                        <div className="text-center mb-4 mt-4">
                                                            <SignaturePad onSave={handleSignatureSave} />
                                                        </div>
                                                        <ContractTemplate {...contractData} />
                                                        {currentUserId && (currentUserId === contractData.ownerId || currentUserId === contractData.contractorId) && (
                                                            <div className="text-center mt-4">
                                                                <SignaturePad onSave={handleSignatureSave} />
                                                            </div>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </TabPane>
                                        </TabContent>
                                    </form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ContractDetail;